/**
 * Client-side ability RAW FACTS per ability — AUTO-DERIVED from content
 * (#412 v1 scope, extended by #461 to also carry the authored tooltip
 * `description` and to be the sole source for cooldown-derived UI),
 * mirroring `abilityCatalog.ts`'s build-time `import.meta.glob` +
 * narrow-schema-strip pattern exactly: read the raw ability JSON text at
 * BUILD TIME, then parse it down to ONLY the whitelisted, non-structurally-
 * complex fields a tooltip can show — never the full balance-sensitive shape
 * (`effects`, `losPolicy`, `facingPolicy`, `positionalPolicy`,
 * `targetHpGate`, `delivery`, etc. are all structurally stripped by the
 * schema below, same as `abilityCatalog.ts`'s `ABILITY_META_SCHEMA`).
 *
 * FIELD SET (confirmed via a scan of every shipped ability's keys):
 * `description`, `rangeUnits`, `minRangeUnits` (when present),
 * `cooldownTicks`, `castTimeTicks`, `cost`, `gcd`, `area.radius` (when an
 * `area` block exists), `charges.max` / `charges.rechargeTicks` (when
 * present). Raw ticks as-is — no unit conversion or pretty-formatting
 * (that's the deferred, templated-prose scope of #412's ORIGINAL body, not
 * this v1). `rangeUnits`/`minRangeUnits`/`area.radius` ARE re-scaled by the
 * #624 range dials (see below) — everything else here is authored-value-
 * as-is.
 *
 * #461: this is now the ONE auto-derived seam for both ability description
 * and cooldown-derived UI, replacing the two hand-maintained, drift-prone
 * tables that used to hold them (`abilityDescriptions.ts`,
 * `abilityCooldowns.ts` — both deleted). There is no balance or competitive-
 * integrity concern with exposing any of this data to the client bundle:
 * every value here is already fully visible to any player the instant the
 * ability is cast in a real match — reading it from content at build time
 * instead of a hand-copied table discloses nothing new.
 *
 * #466: `charges.rechargeTicks` joins `charges.max` for the SAME reason
 * `cooldownTicks` already backs the ordinary cooldown sweep —
 * `computeActionBarState` needs the ability's static recharge DURATION to
 * normalize the live `nextChargeReadyAtTick` (wire, per-tick) into a 0..1
 * sweep fraction, mirroring the existing `readyAtTick` + `cooldownTicks`
 * combination exactly.
 *
 * #502: `appliedAuraDefIds` joins the whitelist — the def id(s) of every
 * TOP-LEVEL `applyAura` effect this ability's `effects` array carries, in
 * authored order. NOT a general effects dump: everything else about an
 * effect (`kind` beyond `"applyAura"`, `damage`/`heal` amounts, targeting,
 * any effect nested inside a wrapper like `self`/`cleave`/`dispel`) stays
 * stripped, same discipline as `area`/`charges` above narrowing to a single
 * sub-field. This is what lets `AbilityTooltip` show "how long does the buff
 * this ability grants last" (resolved via `auraLabels.ts`'s
 * `auraDurationTicks`) BEFORE the ability is ever cast — the generic
 * mechanism behind the "Defensive Strike tooltip should show buff duration"
 * ask, built for every ability, not hand-coded for that one. A nested
 * `applyAura` (inside `self`/`cleave`/etc.) is a known v1 gap — out of scope
 * here, since resolving it needs the FULL effect union, which this
 * intentionally-narrow schema does not import.
 *
 * #624: `rangeUnits`/`minRangeUnits`/`area.radius` are scaled at
 * `@pit/content`'s content-LOAD time (`abilityRangeScaleBp`/
 * `abilityMinRangeScaleBp`/`areaCircleRadiusScaleBp`, basis points) — a
 * value this build-time raw-JSON read would otherwise never see (it reads
 * the AUTHORED, pre-scale JSON straight off disk). Re-applying the SAME
 * `idiv(value * scaleBp, 10000)` scale here — reading `tuning.json`'s raw
 * text via the identical glob pattern, rather than importing
 * `@pit/content`'s loader (deliberately NOT bundled into the browser, see
 * `catalog.ts`) — keeps this facts table byte-identical to what the SERVER
 * actually simulates, instead of silently drifting back to the pre-#624
 * authored numbers the instant a range dial moves off `10000`.
 */

import { z } from "zod";
import { idiv } from "@pit/math";
import { ABILITY_ID_SCHEMA, type AbilityId, type ChampionId } from "@pit/protocol";
import { CHAMPION_SLOTS, type ChampionSlots } from "./abilityCatalog.js";

/** The subset of `Tuning`'s #624 range dials this module needs to keep
 *  `rangeUnits`/`minRangeUnits`/`area.radius` in sync with `@pit/content`'s
 *  scaled runtime values. Absent ⇒ schema default `10000` (1x, a no-op) —
 *  mirrors `TUNING_SCHEMA`'s own defaults so an unset dial in `tuning.json`
 *  never diverges from `@pit/content`'s own default-filling.
 */
const RANGE_TUNING_SCHEMA = z.object({
  abilityRangeScaleBp: z.number().int().default(10000),
  abilityMinRangeScaleBp: z.number().int().default(10000),
  areaCircleRadiusScaleBp: z.number().int().default(10000),
});

/** Build-time read of `tuning.json`'s #624 range-dial values — same
 *  literal-`import.meta.glob` (single-file) rule as every other content
 *  glob in this directory: the call MUST be the literal form for Vite's
 *  static transform. */
function loadRangeTuning(): z.infer<typeof RANGE_TUNING_SCHEMA> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/tuning.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const raw = Object.values(modules)[0];
  if (raw === undefined) {
    throw new Error("tuning.json not found via import.meta.glob");
  }
  return RANGE_TUNING_SCHEMA.parse(JSON.parse(raw) as unknown);
}

const RANGE_TUNING = loadRangeTuning();

/** `idiv(value * scaleBp, 10000)` — the SAME floored-basis-points formula
 *  `@pit/content`'s `rangeScale.ts` applies at load time. */
function scaleRange(value: number, scaleBp: number): number {
  return idiv(value * scaleBp, 10000);
}

/**
 * Narrow ability-facts schema. `z.object` (not `strictObject`) strips
 * `schools`/`targetType`/`losPolicy`/`facingPolicy`/`positionalPolicy`/
 * `targetHpGate`/`delivery`/etc. — none of that (nor any field beyond the
 * whitelist) ever reaches the client bundle. `area`/`charges`/`effects` are
 * narrowed to their single relevant sub-field(s) only; `z.object`'s "ignore
 * unknown keys" behavior means an ability whose `area` is a CONE (no
 * `radius` field) still parses fine, just with `radius` absent, and an
 * `applyAura` entry's `durationPerPoint`/`absorbSeed`/etc. are silently
 * dropped, leaving only `def`.
 */
const ABILITY_FACTS_SCHEMA = z.object({
  id: ABILITY_ID_SCHEMA,
  description: z.string().optional(),
  // #637: `@pit/protocol`'s `ABILITY_SCHEMA.rangeUnits` is now
  // `.default(0)` (a self-targeted ability, e.g. Frost Nova, may omit the
  // dead field entirely) — mirror that default here so this raw-JSON
  // reader stays in sync with the authoritative schema instead of failing
  // to parse ability JSON that legally omits `rangeUnits`.
  rangeUnits: z.number().int().default(0),
  minRangeUnits: z.number().int().optional(),
  cooldownTicks: z.number().int(),
  castTimeTicks: z.number().int(),
  cost: z.number().int(),
  gcd: z.boolean(),
  area: z.object({ radius: z.number().int().optional() }).optional(),
  charges: z.object({ max: z.number().int(), rechargeTicks: z.number().int() }).optional(),
  effects: z
    .array(z.object({ kind: z.string(), def: z.string().optional() }))
    .optional(),
});

/** Auto-derived, whitelisted raw ability facts — never anything beyond the
 *  whitelist above. */
export interface AbilityFacts {
  /** Authored, player-facing tooltip text, or `null` if this ability has no
   *  authored `description` (every SHIPPED ability does — see
   *  `ABILITY_SCHEMA.description`'s doc comment in `@pit/protocol`). */
  readonly description: string | null;
  readonly rangeUnits: number;
  readonly minRangeUnits?: number;
  readonly cooldownTicks: number;
  readonly castTimeTicks: number;
  readonly cost: number;
  readonly gcd: boolean;
  readonly areaRadius?: number;
  readonly maxCharges?: number;
  /** #466: static per-charge recharge duration, ticks — paired with
   *  `maxCharges` (present iff `maxCharges` is present). */
  readonly rechargeTicks?: number;
  /** #502: def id(s) of every top-level `applyAura` effect, authored order.
   *  Absent when the ability applies no aura at top level (see the module
   *  doc comment for the nested-wrapper v1 gap). */
  readonly appliedAuraDefIds?: readonly string[];
}

/**
 * Build-time map of every on-disk ability JSON → its RAW text, parsed down
 * to the v1 whitelisted facts and indexed by `id`. Same literal-
 * `import.meta.glob` rule as `abilityCatalog.ts`: the call MUST be the
 * literal form for Vite's static transform.
 */
function loadContentAbilityFacts(): ReadonlyMap<AbilityId, AbilityFacts> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/abilities/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Map<AbilityId, AbilityFacts>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const parsed = ABILITY_FACTS_SCHEMA.parse(JSON.parse(raw) as unknown);
    const appliedAuraDefIds = (parsed.effects ?? [])
      .filter((e) => e.kind === "applyAura")
      .map((e) => e.def)
      .filter((def): def is string => def !== undefined);
    out.set(parsed.id, {
      description: parsed.description ?? null,
      rangeUnits: scaleRange(parsed.rangeUnits, RANGE_TUNING.abilityRangeScaleBp),
      cooldownTicks: parsed.cooldownTicks,
      castTimeTicks: parsed.castTimeTicks,
      cost: parsed.cost,
      gcd: parsed.gcd,
      ...(parsed.minRangeUnits !== undefined
        ? { minRangeUnits: scaleRange(parsed.minRangeUnits, RANGE_TUNING.abilityMinRangeScaleBp) }
        : {}),
      ...(parsed.area?.radius !== undefined
        ? { areaRadius: scaleRange(parsed.area.radius, RANGE_TUNING.areaCircleRadiusScaleBp) }
        : {}),
      ...(parsed.charges?.max !== undefined ? { maxCharges: parsed.charges.max } : {}),
      ...(parsed.charges?.rechargeTicks !== undefined
        ? { rechargeTicks: parsed.charges.rechargeTicks }
        : {}),
      ...(appliedAuraDefIds.length > 0 ? { appliedAuraDefIds } : {}),
    });
  }
  return out;
}

/** Every content ability's raw facts, keyed by its opaque id. */
export const ABILITY_FACTS_BY_ID: ReadonlyMap<AbilityId, AbilityFacts> =
  loadContentAbilityFacts();

/** Raw facts for the ability at `id`, or `null` if unknown. Pure lookup. */
export function abilityFactsForId(id: AbilityId): AbilityFacts | null {
  return ABILITY_FACTS_BY_ID.get(id) ?? null;
}

/**
 * Champion id → ability FACTS per slot (index i = slot i): resolve each
 * champion's ability id list, then resolve each id to its derived facts via
 * `factsIndex`. A malformed/missing ability reference (id in a champion's
 * `abilities` array with no matching ability JSON) throws LOUD — mirrors
 * `abilityCatalog.ts`'s `deriveAbilityNamesByChampion` exactly, including
 * being exported (pure, no glob I/O) so tests can exercise both the happy
 * path and the throw without needing to corrupt real content.
 */
export function deriveAbilityFactsByChampion(
  championSlots: readonly ChampionSlots[],
  factsIndex: ReadonlyMap<AbilityId, AbilityFacts>,
): ReadonlyMap<ChampionId, readonly AbilityFacts[]> {
  return new Map(
    championSlots.map(({ id: championId, abilities: abilityIds }) => {
      const facts = abilityIds.map((abilityId) => {
        const f = factsIndex.get(abilityId);
        if (f === undefined) {
          throw new Error(
            `champion ${championId} references ability id ${abilityId}, which has ` +
              `no matching ability JSON under packages/content/data/abilities/*.json`,
          );
        }
        return f;
      });
      return [championId, facts];
    }),
  );
}

/**
 * Champion id → ability FACTS per slot (index i = slot i), fully derived
 * from content. Computed eagerly at module load so a malformed/missing
 * ability reference throws LOUD here, at build/test time, rather than
 * lazily on first render (mirrors `ABILITY_NAMES_BY_CHAMPION`).
 */
export const ABILITY_FACTS_BY_CHAMPION: ReadonlyMap<ChampionId, readonly AbilityFacts[]> =
  deriveAbilityFactsByChampion(CHAMPION_SLOTS, ABILITY_FACTS_BY_ID);

/**
 * Raw facts for `slot` on `championId`, resolved via the content-canonical
 * slot order — `null` if the champion is unknown or the slot is empty
 * (out of range for that champion's real ability count).
 */
export function abilityFacts(championId: ChampionId, slot: number): AbilityFacts | null {
  const facts = ABILITY_FACTS_BY_CHAMPION.get(championId);
  if (facts === undefined) return null;
  return facts[slot] ?? null;
}

/**
 * Tooltip description for `slot` on `championId`, or `null` if that slot has
 * no ability or no authored `description` yet. THE single auto-derived
 * source for ability tooltip text (#461) — replaces the deleted
 * `abilityDescriptions.ts` hand-maintained table.
 */
export function abilityDescription(championId: ChampionId, slot: number): string | null {
  return abilityFacts(championId, slot)?.description ?? null;
}
